import React, { useEffect, useCallback, useState, useRef } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import { getQuizOptionsData, PERSONALITY_TITLES, QUIZ_OPTIONS_DATA } from '../../constants'
import { useQuizSelections } from '../../hooks/useLocalStorage'
import stepSisSvg from '../../assets/StepSis.svg'
import stepMomSvg from '../../assets/StepMom.svg'
import girlfriendSvg from '../../assets/Girlfriend.svg'
import sexFriendSvg from '../../assets/SexFriend.svg'
import giftIcon from '../../assets/gift.svg'
import starsIcon from '../../assets/stars.svg'
import LogoSvg from '../../assets/Logo.svg'
import PlayPauseIcon from '../shared/PlayPauseIcon.jsx'
import LazyImage from '../shared/LazyImage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSteps } from '../../hooks/useSteps'
import { trackStep6, trackRegistrationModalOpened } from '../../utils/analytics'
import { useGrowthBookValues } from '../../contexts/GrowthBookContext'
import { createTempSimpleRegistration, createModelParams, LP_SOURCE } from '../../utils/api'
import { buildPaywallUrl } from '../../utils/paywall'

// simple_registration (86ca50q0c): module-scoped dedupe for the temp-user
// creation. Step5 mounts more than once per visit (Home quiz step + /summary
// route + /signup + StrictMode), and a component ref resets on each mount, so the
// guard must live outside the component. sessionStorage persists the resolved
// token across a full remount / reload within the same tab.
let moduleTempRegistrationPromise = null
const TEMP_REG_SESSION_KEY = 'csfw_simple_reg_temp'

// Функція для генерації випадкових виборів з правильними форматами
const generateRandomSelections = () => {
  const getRandomItem = array => array[Math.floor(Math.random() * array.length)]
  const getRandomAge = () => Math.floor(Math.random() * (55 - 21 + 1)) + 21

  // Спочатку обираємо стиль
  const welcomeOptions = QUIZ_OPTIONS_DATA.welcome
  const randomWelcome = getRandomItem(welcomeOptions)
  const selectedStyle = randomWelcome.value

  // Отримуємо опції для обраного стилю
  const ethnicityOptions = QUIZ_OPTIONS_DATA.ethnicity.filter(opt => !opt.style || opt.style === selectedStyle)
  const bodyTypeOptions = QUIZ_OPTIONS_DATA.bodyType.filter(opt => !opt.style || opt.style === selectedStyle)
  const breastSizeOptions = QUIZ_OPTIONS_DATA.breastSize.filter(opt => !opt.style || opt.style === selectedStyle)
  const buttSizeOptions = QUIZ_OPTIONS_DATA.buttSize.filter(opt => !opt.style || opt.style === selectedStyle)
  const hairColorOptions = QUIZ_OPTIONS_DATA.hairColor.filter(opt => !opt.style || opt.style === selectedStyle)
  const hairStyleOptions = QUIZ_OPTIONS_DATA.hairStyle.filter(opt => !opt.style || opt.style === selectedStyle)
  const eyeColorOptions = QUIZ_OPTIONS_DATA.eyeColor.filter(opt => !opt.style || opt.style === selectedStyle)
  const voiceOptions = QUIZ_OPTIONS_DATA.voice.filter(opt => !opt.style || opt.style === selectedStyle)
  const personalityOptions = QUIZ_OPTIONS_DATA.personality

  const randomVoice = getRandomItem(voiceOptions.length > 0 ? voiceOptions : QUIZ_OPTIONS_DATA.voice)

  const randomSelections = {
    welcome: selectedStyle,
    step1: {
      ethnicity: getRandomItem(ethnicityOptions.length > 0 ? ethnicityOptions : QUIZ_OPTIONS_DATA.ethnicity).value,
      age: getRandomAge(),
    },
    step2: {
      bodyType: getRandomItem(bodyTypeOptions.length > 0 ? bodyTypeOptions : QUIZ_OPTIONS_DATA.bodyType).value,
      breastSize: getRandomItem(breastSizeOptions.length > 0 ? breastSizeOptions : QUIZ_OPTIONS_DATA.breastSize).value,
      buttSize: getRandomItem(buttSizeOptions.length > 0 ? buttSizeOptions : QUIZ_OPTIONS_DATA.buttSize).value,
    },
    step3: {
      hairColor: getRandomItem(hairColorOptions.length > 0 ? hairColorOptions : QUIZ_OPTIONS_DATA.hairColor).value,
      hairStyle: getRandomItem(hairStyleOptions.length > 0 ? hairStyleOptions : QUIZ_OPTIONS_DATA.hairStyle).value,
      eyeColor: getRandomItem(eyeColorOptions.length > 0 ? eyeColorOptions : QUIZ_OPTIONS_DATA.eyeColor).value,
    },
    step4: {
      voice: randomVoice.value,
      voiceAudio: randomVoice.audio,
      personality: getRandomItem(personalityOptions).value,
    },
  }

  return randomSelections
}

const Step5 = ({ onAnswer }) => {
  const { t } = useTranslation()
  const { isShortMode } = useSteps()

  const { paywallNavType, paywallFlow, paywallOrder, magicLink } = useGrowthBookValues()
  // Assembled inline the same way the auth components do (createAccount.jsx) —
  // there is no shared useGBValue hook in this repo. buildPaywallPopupRegistrationExtras
  // reads the white nav-config keys, so they must be present.
  const ab_testing = {
    'createsexfriends-white-magicLink': magicLink,
    'createsexfriends-white-paywall-nav-config': paywallNavType,
    'createsexfriends-white-paywall-flow': paywallFlow,
    'createsexfriends-white-paywall-order': paywallOrder,
  }
  const isSimpleRegistration = paywallNavType === 'simple_registration'
  // simple_registration (86ca50q0c): always drive the fake-generation loader — it
  // hides the "Bring to life" button entirely so the user CANNOT escape to /signup
  // before we redirect to the platform paywall. The temp registration is pre-fired
  // during the loader (in the background) and the handoff runs when the fixed 5s
  // loader completes, so the generation animation always plays in full instead of
  // being cut short the instant the temp request resolves. (This repo has no
  // summaryVariations 'loader' arm, so simple_registration is the sole driver.)
  const isLoaderVariant = isSimpleRegistration

  const [fakeProgress, setFakeProgress] = useState(0)
  const [isFakeGenerating, setIsFakeGenerating] = useState(isLoaderVariant)
  // Marks the handoff (temp registration → paywall redirect) as in flight.
  const [isHandingOff, setIsHandingOff] = useState(false)

  useEffect(() => {
    if (isLoaderVariant) setIsFakeGenerating(true)
  }, [isLoaderVariant])

  const [selections, setSelections] = useQuizSelections()
  const [finalSelections, setFinalSelections] = useState(null)
  const location = useLocation()
  const navigate = useNavigate()
  const isHalloween = !!location.pathname.includes('/halloween')
  // Проверяем, есть ли все необходимые данные
  const hasAllSelections = useCallback(selectionsData => {
    // Проверка step1
    const hasStep1 =
      selectionsData?.welcome &&
      selectionsData?.step1?.ethnicity &&
      selectionsData?.step1?.age

    // Проверка step2
    const hasStep2 =
      selectionsData?.step2?.bodyType &&
      selectionsData?.step2?.breastSize &&
      selectionsData?.step2?.buttSize

    const commonSelections = hasStep1 && hasStep2

    // ВАЖНО: Определяем режим по ФАКТИЧЕСКИМ данным, а не по текущему GrowthBook значению!
    // Если есть step3 и step4 - значит квиз проходили в BASE режиме (все степы)
    const hasStep3AndStep4 =
      selectionsData?.step3?.hairColor &&
      selectionsData?.step3?.hairStyle &&
      selectionsData?.step3?.eyeColor &&
      selectionsData?.step4?.voice &&
      selectionsData?.step4?.voiceAudio &&
      selectionsData?.step4?.personality

    // Если есть step3 и step4 - это BASE режим
    if (hasStep3AndStep4) {
      return commonSelections && hasStep3AndStep4
    }

    // Если только step1 и step2 - это SHORT режим (без step3/step4)
    // В SHORT режиме после step2 сразу идет step5
    return commonSelections
  }, [])

  useEffect(() => {

    if (location.pathname === '/signup') {
      const randomSelections = generateRandomSelections()
      setFinalSelections(randomSelections)
      setSelections(randomSelections)
    } else {
      if (hasAllSelections(selections)) {
        // Если это SHORT mode (нет step3/step4), добавляем дефолтные значения
        const hasStep3AndStep4 =
          selections?.step3?.hairColor &&
          selections?.step3?.hairStyle &&
          selections?.step3?.eyeColor &&
          selections?.step4?.voice &&
          selections?.step4?.voiceAudio &&
          selections?.step4?.personality


        if (!hasStep3AndStep4 && isShortMode) {
        
          // SHORT mode - добавляем дефолтные значения для step3/step4
          const selectedStyle = selections?.welcome || 'Realistic'
          const hairColorOptions = QUIZ_OPTIONS_DATA.hairColor.filter(opt => !opt.style || opt.style === selectedStyle)
          const hairStyleOptions = QUIZ_OPTIONS_DATA.hairStyle.filter(opt => !opt.style || opt.style === selectedStyle)
          const eyeColorOptions = QUIZ_OPTIONS_DATA.eyeColor.filter(opt => !opt.style || opt.style === selectedStyle)
          const voiceOptions = QUIZ_OPTIONS_DATA.voice.filter(opt => !opt.style || opt.style === selectedStyle)
          const personalityOptions = QUIZ_OPTIONS_DATA.personality

          const defaultVoice = voiceOptions[0] || QUIZ_OPTIONS_DATA.voice[0]

          setFinalSelections({
            ...selections,
            step3: {
              hairColor: hairColorOptions[0]?.value || QUIZ_OPTIONS_DATA.hairColor[0]?.value,
              hairStyle: hairStyleOptions[0]?.value || QUIZ_OPTIONS_DATA.hairStyle[0]?.value,
              eyeColor: eyeColorOptions[0]?.value || QUIZ_OPTIONS_DATA.eyeColor[0]?.value,
            },
            step4: {
              voice: defaultVoice.value,
              voiceAudio: defaultVoice.audio,
              personality: personalityOptions[0]?.value,
            },
          })
        } else {
    
          setFinalSelections(selections)
        }
      } 
    }
  }, [location.pathname, selections, hasAllSelections, isShortMode, setSelections])

  // Fire `step_6` once the summary is actually rendered (finalSelections ready).
  const step6FiredRef = useRef(false)
  useEffect(() => {
    if (finalSelections?.welcome && !step6FiredRef.current) {
      step6FiredRef.current = true
      trackStep6()
    }
  }, [finalSelections?.welcome])

  const selectedStyle = finalSelections?.welcome || 'Realistic'

  // Derive layout from the data the user actually has, not from the (async) GrowthBook flag.
  // Otherwise on the first /summary navigation the flag has resolved → SHORT layout, but on
  // reload it defaults to BASE — same data, two different summaries.
  const isShortLayout =
    !finalSelections?.step3?.hairColor || !finalSelections?.step4?.voice

  const ethnicities = getQuizOptionsData('ethnicity', selectedStyle)
  const hairColors = getQuizOptionsData('hairColor', selectedStyle)
  const hairStyles = getQuizOptionsData('hairStyle', selectedStyle)
  const bodyTypes = getQuizOptionsData('bodyType', selectedStyle)
  const breastSizes = getQuizOptionsData('breastSize', selectedStyle)
  const bootySizes = getQuizOptionsData('buttSize', selectedStyle)
  const eyeColors = getQuizOptionsData('eyeColor', selectedStyle)

  // Create the dummy user once per visit (deduped via the module-scoped promise +
  // sessionStorage cache above — see the note at their declaration).
  const startTempRegistration = useCallback(() => {
    if (moduleTempRegistrationPromise) return moduleTempRegistrationPromise

    let cached = null
    try {
      cached = sessionStorage.getItem(TEMP_REG_SESSION_KEY)
    } catch {
      // ignore storage failures
    }

    if (cached) {
      moduleTempRegistrationPromise = Promise.resolve(JSON.parse(cached))
    } else {
      moduleTempRegistrationPromise = createTempSimpleRegistration(finalSelections, ab_testing)
        .then(data => {
          if (data?.success && data?.authToken) {
            try {
              sessionStorage.setItem(TEMP_REG_SESSION_KEY, JSON.stringify(data))
            } catch {
              // ignore storage failures
            }
          }
          return data
        })
        .catch(() => {
          // Reset so a retry can re-attempt; caller falls back to /signup on null.
          moduleTempRegistrationPromise = null
          return null
        })
    }
    return moduleTempRegistrationPromise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalSelections, magicLink, paywallNavType, paywallFlow, paywallOrder])

  const goToSignup = useCallback(() => {
    navigate(location.pathname.replace('/summary', '/signup'))
  }, [navigate, location.pathname])

  const handleBringToLife = useCallback(async () => {
    // Non-simple-registration: existing behaviour, untouched.
    if (!isSimpleRegistration) {
      goToSignup()
      return
    }

    if (isHandingOff) return
    setIsHandingOff(true)

    // simple_registration: hand off to the platform paywall with the dummy user's
    // token (auth_flow=tempLogin). Any failure falls back to /signup.
    try {
      const data = await startTempRegistration()
      if (!data?.success || !data?.authToken) {
        goToSignup()
        return
      }

      const modelParams = finalSelections ? await createModelParams(finalSelections) : null
      const style = finalSelections?.welcome || 'Realistic'

      // white's buildPaywallUrl is the 5-arg SFW signature (navType resolved
      // internally via localStorage, protection=sfw baked in). paywallNavType is
      // persisted to localStorage by GrowthBookProvider, so resolvePaywallPath
      // returns /paywall for simple_registration (only chat_paywall → /lobby).
      const paywallUrl = buildPaywallUrl(data.authToken, 'login_paywall', modelParams, style)

      // /paywall is a PLATFORM FRONTEND page (Next :3000 in local dev), while
      // buildPaywallUrl uses SWIPEY_BASE_URL (the API :3001 locally). Repoint the
      // redirect at the platform host — in staging/prod both resolve to the same
      // origin, so this is a no-op there. Mirrors useUpgateCheckout.js's pattern.
      const platformBase =
        process.env.REACT_APP_PLATFORM_BASE_URL || process.env.REACT_APP_SWIPEY_BASE_URL
      const url = new URL(paywallUrl)
      if (platformBase) {
        const platform = new URL(platformBase)
        url.protocol = platform.protocol
        url.host = platform.host
      }

      // auth_flow=tempLogin tells login-paywall to do a side-effect-free session
      // bootstrap for the dummy user; lp_source keeps attribution on the handoff.
      url.searchParams.set('auth_flow', 'tempLogin')
      url.searchParams.set('lp_source', LP_SOURCE)

      // Consume the cached token: if the user comes back and re-runs the quiz in
      // this tab, they must mint a fresh dummy for the new selections.
      try {
        sessionStorage.removeItem(TEMP_REG_SESSION_KEY)
      } catch {
        // ignore storage failures
      }
      moduleTempRegistrationPromise = null

      // Fire the funnel's `lander_registration_modal` event for this cohort's
      // committed handoff ONLY. In the normal flow AuthModal owns this event
      // (fires on open); this arm redirects away here (full-page unload, AuthModal
      // never mounts), so fire it manually right before the redirect. The failure
      // fallback below goes to /signup where AuthModal fires it itself — so the
      // event stays exactly-once on every path (success, failure, non-simple).
      trackRegistrationModalOpened()
      window.location.href = url.toString()
    } catch {
      setIsHandingOff(false)
      goToSignup()
    }
  }, [isSimpleRegistration, isHandingOff, goToSignup, startTempRegistration, finalSelections])

  // Keep a ref to the latest handoff so the once-only loader effect below always
  // invokes the current closure (avoids a stale-data redirect).
  const handleBringToLifeRef = useRef(handleBringToLife)
  handleBringToLifeRef.current = handleBringToLife

  const getSelectedVideo = useCallback(() => {
    if (finalSelections?.welcome === 'Realistic') {
      return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/realistic_createsexfriend_2.mp4'
    } else if (finalSelections?.welcome === 'Anime') {
      return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Anime/anime_createsexfriend.mp4'
    }
    return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/realistic_createsexfriend_2.mp4'
  }, [finalSelections?.welcome])

  const getSelectedPoster = useCallback(() => {
    if (finalSelections?.welcome === 'Realistic') {
      return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/step0-image.jpg'
    } else if (finalSelections?.welcome === 'Anime') {
      return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Anime/step0-image.jpg'
    }
    return 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/step0-image.jpg'
  }, [finalSelections?.welcome])

  const getPersonalityIcon = useCallback(() => {
    const personality = finalSelections?.step4?.personality
    const map = {
      stepSis: { src: stepSisSvg, alt: 'Step Sister' },
      stepMom: { src: stepMomSvg, alt: 'Step Mom' },
      girlfriend: { src: girlfriendSvg, alt: 'Girlfriend' },
      sexFriend: { src: sexFriendSvg, alt: 'Sex Friend' },
    }
    const entry = map[personality] || map.girlfriend
    return (
      <div className='icon-badge'>
        <img src={entry.src} alt={entry.alt} />
      </div>
    )
  }, [finalSelections?.step4?.personality])

  const getPersonalityTitle = useCallback(() => {
    console.log('PERSONALITY_TITLES', PERSONALITY_TITLES)
    const personality = finalSelections?.step4?.personality
    console.log('personality', personality)

    return PERSONALITY_TITLES[personality] || PERSONALITY_TITLES.girlfriend
  }, [finalSelections?.step4?.personality])

  const getEthnicity = useCallback(() => {
    const ethnicity = finalSelections?.step1?.ethnicity
    return (
      ethnicities.find(e => e.value === ethnicity) || {
        image: '/assets/ethnicity-default.png',
        text: 'Unknown',
      }
    )
  }, [finalSelections?.step1?.ethnicity, ethnicities])

  const getVoiceTitle = useCallback(() => {
    const voice = finalSelections?.step4?.voice
    if (voice) {
      const voiceOption = QUIZ_OPTIONS_DATA.voice.find(v => v.value === voice)
      return voiceOption ? t(voiceOption.text) : 'Voice 1'
    }
    return 'Voice 1'
  }, [finalSelections?.step4?.voice, t])

  const getHairColorImage = useCallback(() => {
    const hairColor = finalSelections?.step3?.hairColor
    const found = hairColors.find(h => h.value === hairColor)
    return found ? found.image : '/assets/step3-hair1.png'
  }, [finalSelections?.step3?.hairColor, hairColors])

  const getHairColorTitle = useCallback(() => {
    const hairColor = finalSelections?.step3?.hairColor
    if (hairColor) {
      const hairColorOption = QUIZ_OPTIONS_DATA.hairColor.find(h => h.value === hairColor)
      return hairColorOption ? t(hairColorOption.text) : t('hairColor.black')
    }
    return 'Black'
  }, [finalSelections?.step3?.hairColor, t])

  const getHairStyleImage = useCallback(() => {
    const hairStyle = finalSelections?.step3?.hairStyle
    const found = hairStyles.find(h => h.value === hairStyle)
    return found ? found.image : '/assets/step3-hair1.png'
  }, [finalSelections?.step3?.hairStyle, hairStyles])

  const getHairStyleTitle = useCallback(() => {
    const hairStyle = finalSelections?.step3?.hairStyle
    if (hairStyle) {
      const hairStyleOption = QUIZ_OPTIONS_DATA.hairStyle.find(h => h.value === hairStyle)
      return hairStyleOption ? t(hairStyleOption.text) : t('hairStyle.straight')
    }
    return 'Straight'
  }, [finalSelections?.step3?.hairStyle, t])

  const getBodyTypeImage = useCallback(() => {
    const val = finalSelections?.step2?.bodyType
    const found = bodyTypes.find(b => b.value === val)
    return found ? found.image : '/assets/step2-body1.png'
  }, [finalSelections?.step2?.bodyType, bodyTypes])

  const getBodyTypeTitle = useCallback(() => {
    const bodyType = finalSelections?.step2?.bodyType
    if (bodyType) {
      const bodyTypeOption = QUIZ_OPTIONS_DATA.bodyType.find(b => b.value === bodyType)
      return bodyTypeOption ? t(bodyTypeOption.text) : t('bodyType.slim')
    }
    return 'Slim'
  }, [finalSelections?.step2?.bodyType, t])

  const getBreastSizeImage = useCallback(() => {
    const val = finalSelections?.step2?.breastSize
    const found = breastSizes.find(b => b.value === val)
    return found ? found.image : '/assets/step2-breast1.png'
  }, [finalSelections?.step2?.breastSize, breastSizes])

  const getBreastSizeTitle = useCallback(() => {
    const breastSize = finalSelections?.step2?.breastSize
    if (breastSize) {
      const breastSizeOption = QUIZ_OPTIONS_DATA.breastSize.find(b => b.value === breastSize)
      return breastSizeOption ? t(breastSizeOption.text) : t('breastSize.small')
    }
    return 'Small'
  }, [finalSelections?.step2?.breastSize, t])

  const getBootySizeImage = useCallback(() => {
    const val = finalSelections?.step2?.buttSize
    const found = bootySizes.find(b => b.value === val)
    return found ? found.image : '/assets/step2-booty1.png'
  }, [finalSelections?.step2?.buttSize, bootySizes])

  const getBootySizeTitle = useCallback(() => {
    const bootySize = finalSelections?.step2?.buttSize
    if (bootySize) {
      const bootySizeOption = QUIZ_OPTIONS_DATA.buttSize.find(b => b.value === bootySize)
      return bootySizeOption ? t(bootySizeOption.text) : t('buttSize.small')
    }
    return 'Small'
  }, [finalSelections?.step2?.buttSize, t])

  const getEyeColorTitle = useCallback(() => {
    const eyeColor = finalSelections?.step3?.eyeColor
    if (eyeColor) {
      const eyeColorOption = QUIZ_OPTIONS_DATA.eyeColor.find(e => e.value === eyeColor)
      return eyeColorOption ? t(eyeColorOption.text) : t('eyeColor.brown')
    }
    return 'Brown'
  }, [finalSelections?.step3?.eyeColor, t])

  const getEyeColorImage = useCallback(() => {
    const val = finalSelections?.step3?.eyeColor
    const found = eyeColors.find(e => e.value === val)
    return found ? found.image : '/assets/step3-eye1.png'
  }, [finalSelections?.step3?.eyeColor, eyeColors])

  const getVoiceAudio = useCallback(() => {
    return finalSelections?.step4?.voiceAudio || null
  }, [finalSelections?.step4?.voiceAudio])

  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = React.useRef(null)

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
      audioRef.current.onended = () => setIsPlaying(false)
    }
  }, [isPlaying])

  useEffect(() => {
    if (!isLoaderVariant || !finalSelections?.welcome) return
    // No "already started" ref guard here on purpose: React runs this effect's
    // cleanup before every re-run (and StrictMode mounts it twice in dev), so at
    // most one interval/timeout is ever live. A ref guard would let the cleanup
    // tear the timers down and then block the re-arm, freezing the loader at 0%.

    const durationMs = 5000
    const intervalMs = 50
    const startedAt = Date.now()
    let done = false

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startedAt
      setFakeProgress(Math.min((elapsed / durationMs) * 100, 100))
      if (elapsed >= durationMs) clearInterval(intervalId)
    }, intervalMs)

    const finish = () => {
      if (done) return
      done = true
      clearInterval(intervalId)
      clearTimeout(timeoutId)
      setFakeProgress(100)
      // simple_registration redirects away via window.location, so keep the overlay
      // up through the async handoff rather than flashing the revealed picture for a
      // frame.
      if (!isSimpleRegistration) setIsFakeGenerating(false)
      // Call the latest handoff (via ref) so it reads current selections / GB
      // values, not whatever was captured when this once-only effect first ran.
      handleBringToLifeRef.current()
    }

    // Fixed-duration loader — the SOLE driver of the handoff, so the generation
    // animation always plays its full duration.
    const timeoutId = setTimeout(finish, durationMs)

    // simple_registration: pre-fire the dummy-user creation in the BACKGROUND so the
    // token is ready by the time the loader completes. We deliberately do NOT finish
    // early on resolution — startTempRegistration is deduped via a module promise +
    // sessionStorage cache and can resolve almost instantly (before the overlay even
    // paints on a repeat visit), which would skip the animation entirely. The 5s
    // timeout above drives the handoff; handleBringToLife awaits this same, by-then
    // resolved promise (no added redirect latency) and falls back to /signup on failure.
    if (isSimpleRegistration) {
      startTempRegistration()
    }

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaderVariant, finalSelections?.welcome])

  if (!finalSelections?.welcome) {
    return (
      <div className='quizContentWrapper lastStep'>
        <QuizContent>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>{t('loading')}</p>
          </div>
        </QuizContent>
      </div>
    )
  }
  const renderFakeGenerationOverlay = () => (
    <div className='fake-generation-overlay'>
      <div className='fake-generation-overlay-bg' />
      <div className='fake-generation-content'>
        <div className='fake-generation-progress'>
          <div
            className='fake-generation-progress-fill'
            style={{ width: `${Math.min(fakeProgress, 100)}%` }}
          />
        </div>
        <div className='fake-generation-percent'>{Math.floor(Math.min(fakeProgress, 100))}%</div>
        <div className='fake-generation-label'>{t('setting_the_scene')}</div>
      </div>
    </div>
  )

  return (
    <div className='quizContentWrapper lastStep'>
      <header className='quiz-header'>
        <img src={LogoSvg} alt='Swipey' className='quiz-header-logo' width={100} height={24} />
      </header>
      <div className='progress-stepper-text step5-ribbon'>
        <img src={giftIcon} alt='' width={12} height={12} />
        {t('limited_offer')}{' '}
        <span className='highlight-text'>{t('offer_price')}</span>
        <img src={giftIcon} alt='' width={12} height={12} />
      </div>
      <QuizContent className='step5-design'>
        <QuizTitle>
          {isHalloween ? (
            <>
              {t('your')}
              <span className='specialText'> {t('fantasy_babe_text')} </span>
              {t('is_ready')}
            </>
          ) : (
            t('fantasy_babe')
          )}
        </QuizTitle>
        <div className='result-grid'>
          {isShortLayout ? (
            <>
              {/* SHORT mode: Same structure as BASE, just fewer cards */}
              {/* Left - Main Video */}
              <div className='result-main '>
                {isFakeGenerating ? (
                  <img src={getSelectedPoster()} alt='' className='step0Video' />
                ) : (
                  <video
                    src={getSelectedVideo()}
                    poster={getSelectedPoster()}
                    autoPlay
                    loop
                    muted
                    preload='metadata'
                    playsInline
                    controls={false}
                    className='step0Video'
                  />
                )}
                {isFakeGenerating ? (
                  renderFakeGenerationOverlay()
                ) : (
                  <div className='result-main-label'>{t(finalSelections.welcome)}</div>
                )}
              </div>

              {/* Right - Cards Grid (5 cards total) */}
              <div className='result-cards short-result-cards'>
                {/* Row 1 */}
                <div className='result-card result-card-ethnicity'>
                  <LazyImage src={getEthnicity().image} alt={getEthnicity().text} />
                  <span>{t(getEthnicity().text)}</span>
                </div>
                <div className='result-card'>
                  <LazyImage src={getBodyTypeImage()} alt={getBodyTypeTitle()} />
                  <span>{getBodyTypeTitle()}</span>
                </div>

                {/* Row 2 */}
                <div className='result-card'>
                  <LazyImage src={getBreastSizeImage()} alt={getBreastSizeTitle()} />
                  <span>{getBreastSizeTitle()}</span>
                </div>
                <div className='result-card'>
                  <LazyImage src={getBootySizeImage()} alt={getBootySizeTitle()} />
                  <span>{getBootySizeTitle()}</span>
                </div>

                {/* Row 3 - Age card spans 2 columns */}
                <div className='result-card result-card-age  result-card-wide'>
                  <div className='age-number specialText'>{finalSelections.step1?.age || 22}</div>
                  <span>{t('years')}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* BASE mode: Original layout */}
              {/* Left - Main Video */}
              <div className='result-main'>
                {isFakeGenerating ? (
                  <img src={getSelectedPoster()} alt='' className='step0Video' />
                ) : (
                  <video
                    src={getSelectedVideo()}
                    poster={getSelectedPoster()}
                    autoPlay
                    loop
                    muted
                    preload='metadata'
                    playsInline
                    controls={false}
                    className='step0Video'
                  />
                )}
                {isFakeGenerating ? (
                  renderFakeGenerationOverlay()
                ) : (
                  <div className='result-main-label'>{t(finalSelections.welcome)}</div>
                )}
              </div>

              {/* Right - Cards Grid */}
              <div className='result-cards'>
                {/* Row 1 */}
                <div className='result-card result-card-ethnicity'>
                  <LazyImage src={getEthnicity().image} alt={getEthnicity().text} />
                  <span>{t(getEthnicity().text)}</span>
                </div>
                <div className='result-card result-card-age'>
                  <div className='age-number specialText'>{finalSelections.step1?.age || 22}</div>
                  <span>{t('years')}</span>
                </div>

                {/* Row 2 */}
                <div className='result-card'>
                  <LazyImage src={getBodyTypeImage()} alt={getBodyTypeTitle()} />
                  <span>{getBodyTypeTitle()}</span>
                </div>
                <div className='result-card'>
                  <LazyImage src={getBreastSizeImage()} alt={getBreastSizeTitle()} />
                  <span>{getBreastSizeTitle()}</span>
                </div>

                {/* Row 3 */}
                <div className='result-card'>
                  <LazyImage src={getBootySizeImage()} alt={getBootySizeTitle()} />
                  <span>{getBootySizeTitle()}</span>
                </div>
                <div className='result-card'>
                  <LazyImage src={getHairStyleImage()} alt={getHairStyleTitle()} />
                  <span>{getHairStyleTitle()}</span>
                </div>

                {/* Row 4 */}
                <div className='result-card'>
                  <LazyImage src={getHairColorImage()} alt={getHairColorTitle()} />
                  <span>{getHairColorTitle()}</span>
                </div>
                <div className='result-card'>
                  <LazyImage src={getEyeColorImage()} alt={getEyeColorTitle()} />
                  <span>{getEyeColorTitle()}</span>
                </div>

                {/* Row 5 */}
                <div className='result-card result-card-voice' onClick={togglePlay}>
                  <PlayPauseIcon isPlaying={isPlaying} />
                  <span>{getVoiceTitle()}</span>
                  {getVoiceAudio() && <audio ref={audioRef} src={getVoiceAudio()} preload='metadata' />}
                </div>
                <div className='result-card result-card-personality'>
                  <div className='personality-icon-wrapper'>{getPersonalityIcon()}</div>
                  <span>{t(getPersonalityTitle())}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* CTA Button — hidden entirely for simple_registration (isFakeGenerating is
            always true there), so the user can't click to escape the flow. The
            per-button guards below are a safety net for the first render tick. */}
        <div
          className='cta-container'
          style={isFakeGenerating || isSimpleRegistration ? { visibility: 'hidden' } : undefined}
        >
          <button
            className='bring-to-life-button'
            onClick={isSimpleRegistration ? undefined : handleBringToLife}
            disabled={isFakeGenerating || isHandingOff || isSimpleRegistration}
          >
            <img src={starsIcon} alt='' className='sparkle-icon' width={20} height={20} />
            <span>{isHandingOff || isSimpleRegistration ? t('loading') : t('bring_ai')}</span>
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step5
